import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import {
  CAMERA_STATE,
  EXERCISE_STATE,
  NETWORK_STATE,
  SEVERITY,
  VIDEO_STATE,
  VOICE_STATE,
} from '../connection-status';
import { TONE_STYLES } from './connection-status-tones';
import {
  AlertIcon,
  CameraOffIcon,
  CheckIcon,
  CloseIcon,
  OfflineIcon,
  ReconnectIcon,
  SignalIcon,
  SpinnerIcon,
  VideoOffIcon,
  VoiceOffIcon,
  WeakSignalIcon,
} from './connection-status-icons';

const LABEL_INK = {
  ok: tw`text-success`,
  warning: tw`text-[#8A4B00]`,
  problem: tw`text-[#A61D2A]`,
  info: tw`text-[#3F5670]`,
};

// state -> [tone, icon, status copy key, optional next-step copy key]
const NETWORK_ROWS = {
  [NETWORK_STATE.checking]: ['info', SpinnerIcon, 'checking'],
  [NETWORK_STATE.good]: ['ok', CheckIcon, 'good'],
  [NETWORK_STATE.weak]: ['warning', WeakSignalIcon, 'weak', 'networkWeak'],
  [NETWORK_STATE.offline]: ['problem', OfflineIcon, 'offline', 'networkOffline'],
};

const EXERCISE_ROWS = {
  [EXERCISE_STATE.connecting]: ['info', SpinnerIcon, 'connecting', 'exerciseConnecting'],
  [EXERCISE_STATE.connected]: ['ok', CheckIcon, 'connected'],
  [EXERCISE_STATE.restored]: ['ok', CheckIcon, 'connected'],
  [EXERCISE_STATE.reconnecting]: ['warning', ReconnectIcon, 'reconnecting', 'exerciseReconnecting'],
  [EXERCISE_STATE.failed]: ['problem', AlertIcon, 'interrupted', 'exerciseFailed'],
};

const VOICE_ROWS = {
  [VOICE_STATE.connecting]: ['info', SpinnerIcon, 'connecting'],
  [VOICE_STATE.connected]: ['ok', CheckIcon, 'connected'],
  [VOICE_STATE.reconnecting]: ['warning', ReconnectIcon, 'reconnecting', 'voiceReconnecting'],
  [VOICE_STATE.unavailable]: ['warning', VoiceOffIcon, 'unavailable', 'voiceUnavailable'],
};

const VIDEO_ROWS = {
  [VIDEO_STATE.playing]: ['ok', CheckIcon, 'working'],
  [VIDEO_STATE.loading]: ['info', SpinnerIcon, 'loading', 'videoLoading'],
  [VIDEO_STATE.error]: ['problem', VideoOffIcon, 'notPlaying', 'videoError'],
};

const CAMERA_ROWS = {
  [CAMERA_STATE.working]: ['ok', CheckIcon, 'working'],
  [CAMERA_STATE.restarting]: ['warning', ReconnectIcon, 'restarting', 'cameraRestarting'],
  [CAMERA_STATE.unavailable]: ['warning', CameraOffIcon, 'unavailable', 'cameraUnavailable'],
};

const DetailRow = ({ label, row }) => {
  const { t } = useTranslation('exercise');
  const [tone, Icon, statusKey, hintKey] = row;

  return (
    <li tw="flex gap-3 border-t border-[#ECEEE8] py-3">
      <span
        css={[
          tw`flex h-8 w-8 shrink-0 items-center justify-center rounded-full`,
          TONE_STYLES[tone],
        ]}
      >
        {/* Sized on a wrapper: a css/tw prop on a runtime-chosen component is
            hoisted out of this scope by the styled-components transform. */}
        <span tw="h-[18px] w-[18px]">
          <Icon width="100%" height="100%" />
        </span>
      </span>
      <div tw="min-w-0 flex-1">
        <div tw="flex justify-between gap-2">
          <span tw="text-sm font-semibold text-text_primary">{label}</span>
          <span css={[tw`text-[13px] font-semibold`, LABEL_INK[tone]]}>
            {t(`connectionStatus.details.status.${statusKey}`)}
          </span>
        </div>
        {hintKey && (
          <p tw="m-0 mt-1 text-[13px] leading-[1.5] text-[#565C63]">
            {t(`connectionStatus.details.hint.${hintKey}`)}
          </p>
        )}
      </div>
    </li>
  );
};

/**
 * The "What's happening?" panel: one plain-language row per subject that is
 * part of this session. Not a modal — the exercise keeps running behind it —
 * so there is no focus trap; it takes focus on open, gives it back on close,
 * and Escape closes it.
 *
 * `variant="popover"` hangs under the top bar (desktop / landscape);
 * `variant="sheet"` is a bottom sheet over the reference video (portrait phone).
 */
export const ConnectionDetailsPanel = ({ status, open, onClose, variant = 'popover' }) => {
  const { t, i18n } = useTranslation('exercise');
  const titleId = useId();
  const headingRef = useRef(null);
  const isSheet = variant === 'sheet';

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    headingRef.current?.focus();
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const rows = [
    ['network', NETWORK_ROWS[status.network]],
    ['exercise', EXERCISE_ROWS[status.exercise]],
    ['voice', VOICE_ROWS[status.voice]],
    ['video', VIDEO_ROWS[status.video]],
    ['camera', CAMERA_ROWS[status.camera]],
  ].filter(([, row]) => row);

  const panel = (
    <section
      aria-labelledby={titleId}
      dir={i18n.dir()}
      css={
        isSheet
          ? tw`fixed inset-x-0 bottom-0 z-[600] max-h-[80dvh] overflow-y-auto rounded-t-[20px] bg-white px-[18px] pb-[22px] pt-2 shadow-[0_-12px_30px_rgba(0,0,0,0.2)]`
          : tw`w-[380px] max-w-[calc(100vw-32px)] rounded-[16px] bg-white px-[18px] pb-2 pt-4 shadow-[0_16px_40px_rgba(0,0,0,0.22)]`
      }
    >
      {isSheet && <div tw="mx-auto mb-2.5 h-1 w-10 rounded-sm bg-stroke" aria-hidden="true" />}
      <div tw="flex items-center justify-between pb-2">
        <h2
          id={titleId}
          ref={headingRef}
          tabIndex={-1}
          tw="m-0 text-base font-bold text-text_primary outline-none"
        >
          {t('connectionStatus.details.title')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('connectionStatus.actions.close')}
          tw="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[#F3F4F1] text-text_primary"
        >
          <CloseIcon tw="h-4 w-4" />
        </button>
      </div>
      <ul tw="m-0 list-none p-0">
        {rows.map(([subject, row]) => (
          <DetailRow
            key={subject}
            label={t(`connectionStatus.details.rows.${subject}`)}
            row={row}
          />
        ))}
      </ul>
    </section>
  );

  if (!isSheet) {
    return panel;
  }

  return (
    <>
      {/* Tapping outside closes the sheet; it is not a blocking backdrop. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        tw="fixed inset-0 z-[599] bg-[rgba(20,22,24,0.28)]"
      />
      {panel}
    </>
  );
};

const SEVERITY_DOT = {
  [SEVERITY.warning]: tw`bg-warning`,
  [SEVERITY.problem]: tw`bg-error`,
};

/** Icon button for the top bar's control group; the dot mirrors the worst row. */
export const ConnectionDetailsButton = ({ severity, expanded, onClick, buttonRef }) => {
  const { t } = useTranslation('exercise');
  const severityLabel =
    severity === SEVERITY.ok ? '' : `, ${t(`connectionStatus.details.severity.${severity}`)}`;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-label={`${t('connectionStatus.actions.openDetails')}${severityLabel}`}
      title={t('connectionStatus.actions.openDetails')}
      css={[
        tw`relative flex h-10 w-10 items-center justify-center rounded-[12px] text-white`,
        expanded && tw`bg-white/20`,
      ]}
    >
      <SignalIcon tw="h-[22px] w-[22px]" />
      {SEVERITY_DOT[severity] && (
        <span
          aria-hidden="true"
          css={[
            tw`absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full shadow-[0_0_0_2px_#2b2b2b]`,
            SEVERITY_DOT[severity],
          ]}
        />
      )}
    </button>
  );
};
